/**
 * Rep review — the CS reps' one short knowledge review queue (cs-analysis
 * docs/design-knowledge-miner.md §2.1). Pure helpers behind the Knowledge
 * page's "Rep review" tab; exercised by knowledge_rep_queue.test.js without a
 * backend.
 *
 * Nikhil 2026-10-22: "yes to the rep queue" — ONE short ranked queue, 10 items
 * a day by default, ONE tap per item (Right / Wrong / Not sure, plus an
 * optional corrected text), only five kinds, ranked by how many clients ask;
 * an unanswered item keeps the safe default and drops off after 7 days. No
 * announcement, no notification: the reps find it here.
 *
 * Contract:
 *   GET  /api/v1/knowledge/rep-queue
 *     → { day, budget, counts: { open, offeredToday, answeredToday, pool: {kind: n} },
 *         unroutedContradictions,
 *         items: [{ id, kind, offeredAt, expiresAt, demand: {asks, orgs},
 *                   unit: {id, module, feature, title, body, audience, corroboration, status, confidence} | null,
 *                   before: {title, body} | null,          (served_correction: the text before the check changed it)
 *                   reasons: string[],
 *                   case: {caseId, question, repAnswer, repQuote, kbAnswer, kbCitations, clientName, module, nature, …} | null,
 *                   answer: {verdict, text, byName, at} | null }] }   (server order = the ranking)
 *   POST /api/v1/knowledge/rep-queue/:id/answer  {verdict: right|wrong|unsure, text?}
 *     → { item, effects: {attested[], flagged[], clientSafe[], corrected, undone, triage, errors[]} }
 *   POST /api/v1/knowledge/rep-queue/:id/undo    (a served correction's one-tap undo) → same shape
 */

export const REP_QUEUE_PATH = '/api/v1/knowledge/rep-queue';
export const rep_answer_path = (id) => `${REP_QUEUE_PATH}/${encodeURIComponent(id)}/answer`;
export const rep_undo_path = (id) => `${REP_QUEUE_PATH}/${encodeURIComponent(id)}/undo`;
/** REP_QUEUE.textMax in cs-analysis shared/knowledge-rep-queue.ts — the DTO's MaxLength. */
export const REP_TEXT_MAX = 4000;

const str_or_null = (v) => (typeof v === 'string' && v.trim() ? v.trim() : null);
const int_or_zero = (v) => { const n = Number(v); return Number.isFinite(n) ? Math.max(0, Math.round(n)) : 0; };
const num_or_null = (v) => { if (v === null || v === undefined || v === '') return null; const n = Number(v); return Number.isFinite(n) ? n : null; };
const ts_of = (d) => { const t = d instanceof Date ? d.getTime() : typeof d === 'number' ? d : Date.parse(d); return Number.isFinite(t) ? t : null; };
const plural = (n, word) => `${n} ${word}${n === 1 ? '' : 's'}`;
const has_id = (v) => v !== null && v !== undefined && v !== '';

/**
 * The five kinds, each with the question the card asks and what each button
 * means for it. Session 4 (2026-10-22): the Right card for the safety-screen
 * and unhelpful kinds says plainly "Right = correct AND fine to tell clients";
 * for a changed answer and the weekly sample, Right = correct.
 */
export const REP_KINDS = {
    served_correction: {
        label: 'Changed answer',
        question: 'A check in the product changed what clients are told. Is the new answer correct?',
        right: 'Right = correct',
        wrong: 'Wrong = undo: clients get the previous answer back at once',
        text: 'Or write the correct answer — clients get it at once',
    },
    unhelpful: {
        label: 'Marked unhelpful',
        question: 'Two client users marked this answer unhelpful. Is it correct, and fine to tell clients?',
        right: 'Right = correct AND fine to tell clients',
        wrong: 'Wrong = flagged: it comes off the client tier',
        text: 'Or write the correct answer — clients get it at once',
    },
    contradiction: {
        label: 'Rep vs knowledge',
        question: 'The knowledge base and a rep answered a client differently, and no product check can settle it. Is the knowledge base right?',
        right: 'Right = the knowledge base is right',
        wrong: 'Wrong = the rep was right',
        text: 'Note (optional) — why, or what the right answer is',
    },
    unsure: {
        label: 'Safety screen unsure',
        question: 'Clients ask about this, and the safety screen was not sure it may be told to clients. Is it correct, and fine to tell clients?',
        right: 'Right = correct AND fine to tell clients',
        wrong: 'Wrong = flagged: it stays internal',
        text: 'Or write the correct text — it replaces this one at once',
    },
    weekly_sample: {
        label: 'Weekly sample',
        question: 'This reached clients this week without a rep. Is it correct?',
        right: 'Right = correct',
        wrong: 'Wrong = flagged: it comes off the client tier',
        text: 'Or write the correct answer — clients get it at once',
    },
};
export const REP_KIND_KEYS = Object.keys(REP_KINDS);
export const REP_VERDICTS = { right: 'Right', wrong: 'Wrong', unsure: 'Not sure' };
export const REP_VERDICT_KEYS = Object.keys(REP_VERDICTS);

export const REP_INTRO = 'A few short checks a day, the ones clients ask about most. One tap each. Anything you skip keeps the safe default and drops off after 7 days — nothing here is a backlog.';
export const REP_FOOTER = 'Answers are recorded with your name. Right confirms the fact under your name; Wrong flags it (a changed answer is undone); a corrected text replaces it at once and the product checks look at it again. Not sure changes nothing.';
export const REP_EMPTY = 'Nothing to check right now.';

const normalize_unit = (u) => (u && has_id(u.id) ? {
    id: String(u.id),
    module: str_or_null(u.module),
    feature: str_or_null(u.feature),
    title: str_or_null(u.title) || '',
    body: typeof u.body === 'string' ? u.body : '',
    audience: str_or_null(u.audience),
    corroboration: str_or_null(u.corroboration),
    status: str_or_null(u.status),
    confidence: num_or_null(u.confidence),
} : null);

const normalize_text = (t) => (t && typeof t.body === 'string' ? { title: str_or_null(t.title) || '', body: t.body } : null);

const normalize_rep_case = (c) => (c && has_id(c.caseId) ? {
    caseId: String(c.caseId),
    question: typeof c.question === 'string' ? c.question : '',
    repAnswer: typeof c.repAnswer === 'string' ? c.repAnswer : '',
    repQuote: str_or_null(c.repQuote),
    kbAnswer: typeof c.kbAnswer === 'string' ? c.kbAnswer : '',
    clientName: str_or_null(c.clientName),
    module: str_or_null(c.module),
    nature: str_or_null(c.nature),
    kbCitations: (Array.isArray(c.kbCitations) ? c.kbCitations : []).filter((k) => k && has_id(k.id)).map((k) => ({ id: String(k.id), title: str_or_null(k.title) || String(k.id) })),
} : null);

export const normalize_rep_answer = (a) => (a && REP_VERDICTS[a.verdict] ? { verdict: a.verdict, text: str_or_null(a.text), byName: str_or_null(a.byName), at: str_or_null(a.at) } : null);

export const normalize_rep_item = (it) => ({
    id: String(it.id),
    kind: REP_KINDS[it.kind] ? it.kind : null,
    offeredAt: str_or_null(it.offeredAt),
    expiresAt: str_or_null(it.expiresAt),
    demand: { asks: int_or_zero(it.demand && it.demand.asks), orgs: int_or_zero(it.demand && it.demand.orgs) },
    unit: normalize_unit(it.unit),
    before: normalize_text(it.before),
    reasons: (Array.isArray(it.reasons) ? it.reasons : []).map(str_or_null).filter(Boolean),
    case: normalize_rep_case(it.case),
    answer: normalize_rep_answer(it.answer),
});

/** The response, tolerant of a missing field; an item of an unknown kind (a newer backend) is left out. */
export const normalize_rep_queue = (resp) => {
    const r = resp || {};
    const c = r.counts || {};
    const pool = c.pool && typeof c.pool === 'object' ? c.pool : {};
    return {
        day: str_or_null(r.day),
        budget: int_or_zero(r.budget),
        counts: {
            open: int_or_zero(c.open),
            offeredToday: int_or_zero(c.offeredToday),
            answeredToday: int_or_zero(c.answeredToday),
            pool: REP_KIND_KEYS.reduce((o, k) => ({ ...o, [k]: int_or_zero(pool[k]) }), {}),
        },
        unroutedContradictions: int_or_zero(r.unroutedContradictions),
        items: (Array.isArray(r.items) ? r.items : []).filter((it) => it && has_id(it.id)).map(normalize_rep_item).filter((it) => it.kind),
    };
};

/** "asked by 3 orgs · 12 times in 30 days" — the demand the card is ranked by. */
export const demand_text = (demand) => {
    const d = demand || {};
    const asks = int_or_zero(d.asks);
    const orgs = int_or_zero(d.orgs);
    if (!asks && !orgs) return 'no client asks recorded yet';
    if (!orgs) return `asked ${plural(asks, 'time')} in 30 days`;
    return `asked by ${plural(orgs, 'org')} · ${plural(asks, 'time')} in 30 days`;
};

/** "drops off in 5 days" / "drops off today"; '' without a date. */
export const expiry_text = (expires_at, now = Date.now()) => {
    const t = ts_of(expires_at);
    const n = ts_of(now);
    if (t === null || n === null) return '';
    const days = Math.ceil((t - n) / 86400000);
    if (days <= 0) return 'drops off today';
    return `drops off in ${plural(days, 'day')}`;
};

/** A typed text is sent with Wrong on a unit card, with any answer on a contradiction (its note). */
export const text_sent_with = (kind, verdict) => kind === 'contradiction' || verdict === 'wrong';

export const rep_answer_body = (kind, verdict, text) => {
    const t = str_or_null(text);
    return t && text_sent_with(kind, verdict) ? { verdict, text: t.slice(0, REP_TEXT_MAX) } : { verdict };
};

/** The Wrong button's label: a typed correction is what Wrong sends; a changed answer's plain Wrong is its undo. */
export const wrong_label = (kind, text) => {
    if (str_or_null(text) && kind !== 'contradiction') return 'Wrong — use my text';
    return kind === 'served_correction' ? 'Wrong — undo' : REP_VERDICTS.wrong;
};

/** What the rep did, as the card shows it once answered. `local` = {verdict, text, pending, effects}. */
export const rep_receipt = (local, kind) => {
    if (!local) return null;
    const v = local.verdict;
    const e = local.effects || {};
    const typed = !!str_or_null(local.text) && kind !== 'contradiction';
    if (local.pending) return { tone: 'muted', text: v === 'right' ? '✓ recording…' : v === 'wrong' ? '✗ recording…' : 'recording…' };
    const errors = Array.isArray(e.errors) && e.errors.length ? ` (${plural(e.errors.length, 'step')} failed: ${e.errors.join('; ')})` : '';
    if (v === 'unsure') return { tone: 'muted', text: 'Not sure — nothing changes' + errors };
    if (kind === 'contradiction') {
        return { tone: v === 'right' ? 'good' : 'bad', text: (v === 'right' ? '✓ The knowledge base is right' : '✗ The rep was right') + errors };
    }
    if (v === 'right') {
        const note = kind === 'unsure' || kind === 'unhelpful' ? ' — confirmed, and fine to tell clients' : ' — confirmed';
        return { tone: 'good', text: '✓ Right' + note + errors };
    }
    if (typed) return { tone: 'good', text: '✎ Corrected — your text is served now' + errors };
    if (kind === 'served_correction') return { tone: 'bad', text: e.undone || !errors ? '↶ Undone — clients get the previous answer' + errors : '✗ Could not undo' + errors };
    return { tone: 'bad', text: '✗ Wrong — flagged' + errors };
};

/** The FYI line for kind a while the contradictions are not routed yet (no items: nothing is guessed). */
export const unrouted_text = (n) => {
    const k = int_or_zero(n);
    if (!k) return null;
    return `FYI: ${plural(k, 'open rep-vs-knowledge case')} ${k === 1 ? 'is' : 'are'} not routed yet — ${k === 1 ? 'it joins' : 'they join'} this list once routing is on (the ones no product check can settle).`;
};

export const rep_dom_id = (id) => `rep-review-${id}`;

/** Open items net of the ones answered on this page since the load — the tab's count. */
export const rep_open_count = (data, answers) => {
    if (!data) return null;
    const answered = data.items.filter((it) => it.answer || (answers && answers[it.id] && !answers[it.id].pending && !answers[it.id].error)).length;
    return Math.max(0, data.items.length - answered);
};
